import React, { useState, useEffect } from 'react';
import {
  Copy,
  Check,
  MessageSquare,
  Mail,
  Share2,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';

/**
 * AmbassadorShareCard — premium, mobile-first share UX for ambassadors.
 *
 * Priorities (top-to-bottom = highest conversion → lowest friction):
 *  1. Big native "Share" button (navigator.share) — Messages/AirDrop/Gmail/Telegram
 *  2. Text Message (SMS / iMessage) — sms: URL scheme
 *  3. WhatsApp — wa.me link (huge in LATAM US market)
 *  4. Email — mailto: link
 *  5. "More options" disclosure → Instagram, TikTok, Facebook, X
 *
 * All buttons reuse the existing `onShare(platform)` callback so the rewards
 * points/ledger backend hooks still fire (`POST /rewards/share`).
 */

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const AmbassadorShareCard = ({
  referralCode,
  shareUrl,
  onShare, // (platform: string) => void
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);

  useEffect(() => {
    setHasNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const shortMessage = `Hey! Do you accept NoFee? Sign up with my code ${referralCode} — restaurants pay $0 commission. ${shareUrl}`;
  const emailSubject = 'Save more on every order with NoFee';
  const emailBody = `Hi,\n\nI thought you'd want to see this — NoFee is a 0% commission ordering platform for restaurants. Use my referral code ${referralCode} when you sign up:\n\n${shareUrl}\n\nIt replaces Uber Eats / DoorDash and the restaurant keeps 100% of every sale.\n\nLet me know what you think!`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareUrl} — code: ${referralCode}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      onShare?.('copy');
      return;
    }
    try {
      await navigator.share({
        title: 'NoFee — 0% commission ordering',
        text: shortMessage,
        url: shareUrl,
      });
      onShare?.('native_share');
    } catch (err) {
      // user cancelled — silent
    }
  };

  const openSMS = () => {
    // iOS: sms:&body=...   ·   Android: sms:?body=...
    // Using `?` works on both modern iOS (16+) and Android. Fallback `&` for older iOS.
    const body = encodeURIComponent(shortMessage);
    const separator = isIOS() ? '&' : '?';
    window.location.href = `sms:${separator}body=${body}`;
    onShare?.('sms');
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shortMessage)}`, '_blank', 'noopener');
    onShare?.('whatsapp');
  };

  const openEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    onShare?.('email');
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="ambassador-share-card">
      {/* Strong headline CTA */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold-400">
          Invite by text & earn rewards
        </p>
        <h3 className="mt-1 text-xl sm:text-2xl font-black text-white tracking-tight">
          One text. $20 closer to your next payout.
        </h3>
        <p className="mt-1.5 text-[13px] text-white/55 leading-relaxed">
          Send your link to a restaurant owner you know. When they activate, you earn $20.
        </p>
      </div>

      {/* Link row */}
      <div className="flex items-center gap-2 p-3 rounded-xl bg-black/50 border border-gold-500/20">
        <ExternalLink className="w-4 h-4 text-gold-400 shrink-0" />
        <input
          readOnly
          value={shareUrl}
          className="flex-1 bg-transparent text-xs sm:text-sm outline-none text-white/80 min-w-0"
          data-testid="ambassador-share-url"
          onClick={(e) => e.target.select()}
        />
        <button
          onClick={copyLink}
          data-testid="ambassador-copy-link"
          className="inline-flex items-center gap-1 text-xs font-bold text-gold-400 hover:text-gold-300 shrink-0 px-2"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Primary actions — mobile-first stack */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {hasNativeShare && (
          <button
            onClick={handleNativeShare}
            data-testid="ambassador-share-native"
            className="col-span-1 sm:col-span-2 inline-flex items-center justify-center gap-2 w-full px-5 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 active:from-amber-400 active:to-gold-600 text-black font-extrabold text-[15px] transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
          >
            <Share2 className="w-4 h-4" />
            Share with a Restaurant
          </button>
        )}

        <button
          onClick={openSMS}
          data-testid="ambassador-share-sms"
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] active:bg-white/[0.14] border border-white/15 hover:border-emerald-400/40 text-white font-bold text-[14px] transition-all"
        >
          <MessageSquare className="w-4 h-4 text-emerald-300" />
          Text Message
        </button>

        <button
          onClick={openWhatsApp}
          data-testid="ambassador-share-whatsapp"
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] active:bg-white/[0.14] border border-white/15 hover:border-green-400/40 text-white font-bold text-[14px] transition-all"
        >
          {/* WhatsApp glyph */}
          <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
          </svg>
          WhatsApp
        </button>

        <button
          onClick={openEmail}
          data-testid="ambassador-share-email"
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] active:bg-white/[0.14] border border-white/15 hover:border-sky-400/40 text-white font-bold text-[14px] transition-all"
        >
          <Mail className="w-4 h-4 text-sky-300" />
          Email
        </button>

        <button
          onClick={copyLink}
          data-testid="ambassador-share-copy"
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] active:bg-white/[0.14] border border-white/15 hover:border-gold-400/40 text-white font-bold text-[14px] transition-all"
        >
          {copied ? (
            <Check className="w-4 h-4 text-emerald-400" />
          ) : (
            <Copy className="w-4 h-4 text-gold-300" />
          )}
          {copied ? 'Copied' : 'Copy Link'}
        </button>
      </div>

      {/* Social — collapsed by default */}
      <div className="pt-2">
        <button
          onClick={() => setShowMore(!showMore)}
          data-testid="ambassador-share-more-toggle"
          className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] text-white/50 hover:text-gold-300 transition-colors"
          aria-expanded={showMore}
        >
          {showMore ? 'Hide social options' : 'More sharing options'}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
        </button>

        {showMore && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ambassador-share-social">
            {[
              ['instagram', 'Instagram'],
              ['tiktok', 'TikTok'],
              ['facebook', 'Facebook'],
              ['twitter', 'X'],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => {
                  if (id === 'facebook') {
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                      '_blank',
                      'noopener'
                    );
                  } else if (id === 'twitter') {
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortMessage)}`,
                      '_blank',
                      'noopener'
                    );
                  } else {
                    // Instagram / TikTok — copy link & flag
                    copyLink();
                  }
                  onShare?.(id);
                }}
                data-testid={`ambassador-share-${id}`}
                className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 hover:text-white text-[12px] font-semibold transition-all"
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmbassadorShareCard;
